import React from "react";
import { Dropdown, Button, ButtonGroup } from "react-bootstrap";

type DateLike = string | Date | null | undefined;

export interface DateRangeValue {
  startDate: string;
  endDate: string;
}

export interface DateRangePreset {
  key: string;
  label: string;
  getRange: () => { start: Date; end: Date };
}

interface DateRangePickerProps {
  startDate?: DateLike;
  endDate?: DateLike;
  onChange: (range: DateRangeValue) => void;
  placeholder?: string;
  locale?: string;
  disabled?: boolean;
  min?: string;
  max?: string;
  size?: "sm" | "lg";
  className?: string;
  menuClassName?: string;
  toggleVariant?: string;
  withPresets?: boolean;
  presets?: DateRangePreset[];
  ariaLabel?: string;
  leadingIcon?: React.ReactNode;
}

const toISODate = (value: DateLike): string => {
  if (!value) return "";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "";
  // Use local calendar date (not UTC) to avoid day-shift bugs.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const parseISODate = (value: string): Date | null => {
  if (!value) return null;
  const parts = value.split("-").map(Number);
  if (parts.length !== 3) return null;
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  return Number.isNaN(d.getTime()) ? null : d;
};

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const isBetweenInclusive = (d: Date, a: Date, b: Date) => d >= a && d <= b;

const formatDateLabel = (value: DateLike, locale: string): string => {
  if (!value) return "";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(locale || "tr-TR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
};

const defaultPresets: DateRangePreset[] = [
  {
    key: "today",
    label: "Today",
    getRange: () => {
      const end = new Date();
      const start = new Date(end);
      return { start, end };
    },
  },
  {
    key: "7d",
    label: "Last 7 Days",
    getRange: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 6);
      return { start, end };
    },
  },
  {
    key: "30d",
    label: "Last 30 Days",
    getRange: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 29);
      return { start, end };
    },
  },
  {
    key: "thisMonth",
    label: "This Month",
    getRange: () => {
      const end = new Date();
      const start = new Date(end.getFullYear(), end.getMonth(), 1);
      return { start, end };
    },
  },
  {
    key: "lastMonth",
    label: "Last Month",
    getRange: () => {
      const end = new Date();
      const firstOfThis = new Date(end.getFullYear(), end.getMonth(), 1);
      const start = new Date(firstOfThis);
      start.setMonth(start.getMonth() - 1);
      const lastOfPrev = new Date(firstOfThis);
      lastOfPrev.setDate(0);
      return { start, end: lastOfPrev };
    },
  },
];

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  placeholder = "Select date range",
  locale = "tr-TR",
  disabled,
  min,
  max,
  size,
  className,
  menuClassName,
  toggleVariant = "outline-secondary",
  withPresets = true,
  presets,
  ariaLabel = "Date range picker",
  leadingIcon,
}) => {
  const [open, setOpen] = React.useState(false);
  const [tempStart, setTempStart] = React.useState<string>(
    toISODate(startDate || "")
  );
  const [tempEnd, setTempEnd] = React.useState<string>(
    toISODate(endDate || "")
  );
  const [monthCursor, setMonthCursor] = React.useState<Date>(() => {
    const base = toISODate(startDate || "") || toISODate(endDate || "");
    return startOfMonth(parseISODate(base || "") || new Date());
  });

  React.useEffect(() => {
    if (open) {
      setTempStart(toISODate(startDate || ""));
      setTempEnd(toISODate(endDate || ""));
    }
  }, [open, startDate, endDate]);

  React.useEffect(() => {
    setTempStart(toISODate(startDate || ""));
  }, [startDate]);

  React.useEffect(() => {
    setTempEnd(toISODate(endDate || ""));
  }, [endDate]);

  React.useEffect(() => {
    if (!open) return;
    const base = toISODate(startDate || "") || toISODate(endDate || "");
    setMonthCursor(startOfMonth(parseISODate(base || "") || new Date()));
  }, [open, startDate, endDate]);

  const hasBoth = !!(tempStart && tempEnd);
  const selectedLabel = hasBoth
    ? `${formatDateLabel(tempStart, locale)} — ${formatDateLabel(tempEnd, locale)}`
    : placeholder;

  const computedPresets =
    presets && presets.length > 0 ? presets : defaultPresets;

  const rangeInvalid =
    !tempStart || !tempEnd || new Date(tempStart) > new Date(tempEnd);

  const commitRange = (start: string, end: string, shouldClose = true) => {
    if (!start || !end) return;
    if (new Date(start) > new Date(end)) return;
    onChange({ startDate: start, endDate: end });
    if (shouldClose) setOpen(false);
  };

  const minDate = parseISODate(min || "");
  const maxDate = parseISODate(max || "");
  const selectedStart = parseISODate(tempStart);
  const selectedEnd = parseISODate(tempEnd);

  const isDisabledDate = (d: Date) => {
    if (minDate && d < minDate) return true;
    if (maxDate && d > maxDate) return true;
    return false;
  };

  const handlePickDate = (d: Date) => {
    if (isDisabledDate(d)) return;
    const picked = toISODate(d);
    if (!selectedStart || (selectedStart && selectedEnd)) {
      setTempStart(picked);
      setTempEnd("");
      return;
    }
    if (d < selectedStart) {
      setTempStart(picked);
      const end = toISODate(selectedStart);
      setTempEnd(end);
      commitRange(picked, end);
    } else {
      setTempEnd(picked);
      commitRange(tempStart, picked);
    }
  };

  const monthLabel = new Intl.DateTimeFormat(locale || "tr-TR", {
    year: "numeric",
    month: "long",
  }).format(monthCursor);

  const weekdayLabels = Array.from({ length: 7 }, (_, i) => {
    const ref = new Date(2026, 0, 4 + i); // Sunday -> Saturday seed
    return new Intl.DateTimeFormat(locale || "tr-TR", { weekday: "short" }).format(ref);
  });

  const calendarCells = React.useMemo(() => {
    const monthStart = startOfMonth(monthCursor);
    const firstWeekday = monthStart.getDay();
    const gridStart = new Date(monthStart);
    gridStart.setDate(monthStart.getDate() - firstWeekday);

    return Array.from({ length: 42 }, (_, idx) => {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + idx);
      const isCurrentMonth = d.getMonth() === monthCursor.getMonth();
      const disabled = isDisabledDate(d);
      const isStart = selectedStart ? sameDay(d, selectedStart) : false;
      const isEnd = selectedEnd ? sameDay(d, selectedEnd) : false;
      const inRange = selectedStart && selectedEnd ? isBetweenInclusive(d, selectedStart, selectedEnd) : false;
      return { d, isCurrentMonth, disabled, isStart, isEnd, inRange };
    });
  }, [monthCursor, min, max, tempStart, tempEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => {
    setTempStart("");
    setTempEnd("");
  };

  const applyPreset = (p: DateRangePreset) => {
    const { start, end } = p.getRange();
    const s = toISODate(start);
    const e = toISODate(end);
    setTempStart(s);
    setTempEnd(e);
    commitRange(s, e);
  };

  return (
    <Dropdown
      show={open}
      onToggle={(s) => setOpen(!!s)}
      className={`drp-root ${className || ""}`}
    >
      <Dropdown.Toggle
        variant={toggleVariant}
        size={size}
        disabled={disabled}
        className="drp-toggle h-38"
        aria-label={ariaLabel}
        aria-expanded={open}
      >
        {leadingIcon ? <span className="drp-leading-icon">{leadingIcon}</span> : null}
        <span className={`drp-label ${hasBoth ? "" : "text-muted"}`}>{selectedLabel}</span>
      </Dropdown.Toggle>

      <Dropdown.Menu
        className={`drp-menu ${menuClassName || ""}`}
        align="start"
      >
        <div
          className="d-flex flex-column gap-2"
          role="group"
          aria-label="Date range panel"
        >
          {withPresets && (
            <div className="mb-1">
              <ButtonGroup className="filter-preset-buttons">
                {computedPresets.map((p) => (
                  <Button
                    key={p.key}
                    variant="outline-secondary"
                    onClick={() => applyPreset(p)}
                    className="preset-btn filter-preset-btn"
                  >
                    {p.label}
                  </Button>
                ))}
              </ButtonGroup>
            </div>
          )}

          <div className="drp-calendar-wrap">
            <div className="drp-cal-head">
              <button type="button" className="drp-nav-btn" onClick={() => setMonthCursor((m) => addMonths(m, -1))} aria-label="Previous month">
                ‹
              </button>
              <div className="drp-cal-title">{monthLabel}</div>
              <button type="button" className="drp-nav-btn" onClick={() => setMonthCursor((m) => addMonths(m, 1))} aria-label="Next month">
                ›
              </button>
            </div>
            <div className="drp-weekdays">
              {weekdayLabels.map((w) => (
                <div key={w} className="drp-weekday">{w}</div>
              ))}
            </div>
            <div className="drp-days-grid">
              {calendarCells.map(({ d, isCurrentMonth, disabled, isStart, isEnd, inRange }) => (
                <button
                  key={d.toISOString()}
                  type="button"
                  className={`drp-day ${!isCurrentMonth ? "outside" : ""} ${inRange ? "in-range" : ""} ${isStart ? "range-start" : ""} ${isEnd ? "range-end" : ""}`}
                  disabled={disabled}
                  onClick={() => handlePickDate(d)}
                >
                  {d.getDate()}
                </button>
              ))}
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-2">
            <Button
              variant="outline-secondary"
              onClick={reset}
              className="btn-30"
              aria-label="Reset date range"
            >
              Reset
            </Button>
          </div>
        </div>
      </Dropdown.Menu>

      <style>{`
        /* Toggle button and control standardization */
        .drp-toggle.h-38 {
          height: 38px !important;
          display: inline-flex;
          align-items: center;
          border-radius: 10px;
          padding: 0 12px;
          line-height: 1;
        }
        .drp-label {
          font-size: 14px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
          display: inline-block;
        }
        .drp-menu {
          min-width: 360px;
        }

        .drp-calendar-wrap {
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          padding: 8px;
          background: #fff;
        }
        .drp-cal-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;
        }
        .drp-cal-title {
          font-size: 13px;
          font-weight: 700;
        }
        .drp-nav-btn {
          border: 1px solid #e5e7eb;
          background: #fff;
          border-radius: 8px;
          width: 28px;
          height: 28px;
          line-height: 1;
        }
        .drp-weekdays, .drp-days-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 4px;
        }
        .drp-weekday {
          text-align: center;
          font-size: 11px;
          color: #6b7280;
          font-weight: 600;
          text-transform: uppercase;
          padding: 2px 0;
        }
        .drp-day {
          border: 0;
          background: transparent;
          height: 30px;
          border-radius: 8px;
          font-size: 12px;
        }
        .drp-day:hover:not(:disabled) {
          background: #f3f4f6;
        }
        .drp-day.outside {
          color: #9ca3af;
        }
        .drp-day.in-range {
          background: rgba(82, 29, 179, 0.08);
          color: #3b2a64;
        }
        .drp-day.range-start,
        .drp-day.range-end {
          background: #521db3;
          color: #fff;
          font-weight: 700;
        }
        .drp-day:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }

        /* Inputs 38px height harmony */
        .control-38 {
          height: 38px !important;
          padding-top: 7px;
          padding-bottom: 7px;
          font-size: 14px;
          line-height: 22px;
        }

        /* Preset buttons compact */
        .preset-btn {
          padding: 2px 10px;
          height: 28px;
          line-height: 24px;
        }

        /* Footer action buttons: compact 30px like other presets */
        .btn-30 {
          height: 30px;
          padding: 0 10px;
          border-radius: 10px;
          font-weight: 600;
        }

        /* Responsive */
        @media (max-width: 420px) {
          .drp-menu { min-width: 280px; }
        }
      `}</style>
    </Dropdown>
  );
};

export default DateRangePicker;
